import heic2any from "heic2any";
import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const EditProductModal = ({
  isOpen,
  onClose,
  product,
  editProduct,
  handleEditChange,
  handleUpdateEditProduct,
  singleImage,
  setSingleImage,
  description,
  internalNotes,
  selectedPricingValue,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingSingleImage, setIsLoadingSingleImage] = useState(false);

  // Handle drag over for single image
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  // Handle drag leave for single image
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Handle file drop for single image
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      await handleFile(file, setIsLoadingSingleImage, setSingleImage);
      e.dataTransfer.clearData();
    }
  };

  // Handle file selection for single image
  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      await handleFile(file, setIsLoadingSingleImage, setSingleImage);
    }
  };

  // Handle file processing (single or multiple)
  const handleFile = async (file, setIsLoading, setImageState) => {
    setIsLoading(true);

    if (!isOpen || !product) return null;

    try {
      if (
        file.name.toLowerCase().endsWith(".heic") ||
        file.name.toLowerCase().endsWith(".heif")
      ) {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        const convertedFile = new File(
          [convertedBlob],
          file.name.replace(/\.heic$|\.heif$/i, ".jpeg"),
          { type: "image/jpeg" }
        );

        setImageState(convertedFile);
      } else {
        setImageState(file);
      }
    } catch (error) {
      console.error("Error processing file:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
  ];

  // Handle Quill editor change
  const handleDescriptionChange = (content) => {
    handleEditChange({
      target: {
        name: "description",
        value: content,
      },
    });
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center font-roboto z-50 bg-black bg-opacity-50">
      {/* Modal Container */}
      <div className="bg-gray-100 rounded-2xl shadow-2xl w-[80%] ">
        {/* Header */}
        <div className="flex flex-row justify-between items-center px-6 py-4 bg-gradient-to-r bg-blue-600 sticky top-0 z-10">
          <h2 className="text-xl font-semibold text-white">Edit Product</h2>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="w-9 h-9 rounded-full flex justify-center items-center transition hover:bg-blue-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col gap-6 max-h-[60vh] overflow-y-auto">
          {/* Quantity and Pricing */}
          <div className="grid grid-cols-3  gap-6 w-full">
            {/* Title Input */}
            <div className="w-full">
              <label className="block font-roboto text-md text-gray-900 mb-2">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={editProduct.title}
                onChange={handleEditChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Enter title"
              />
            </div>
            <div className="w-full">
              <label className="block font-roboto text-md text-gray-900 mb-2">
                Quantity
              </label>
              <input
                type="number"
                name="quantity"
                value={editProduct.quantity ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    handleEditChange(e);
                    return;
                  }
                  const parsedValue = parseInt(value, 10);
                  if (!isNaN(parsedValue) && parsedValue >= 0) {
                    handleEditChange(e);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Enter quantity"
              />
            </div >
            <div className="w-full">
              <label className="block font-roboto text-md text-gray-900 mb-2">
                Per day pricing
              </label>
              <input
                type="number"
                name="selectedPricingValue"
                value={editProduct.selectedPricingValue ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    handleEditChange(e);
                    return;
                  }
                  const parsedValue = parseInt(value, 10);
                  if (!isNaN(parsedValue) && parsedValue >= 0) {
                    handleEditChange(e);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Enter per day pricing"
              />
            </div>
          </div>

          <div className="flex flex-row gap-5 w-full">
            {/* Description Editor */}
          <div className="w-full">
            <label className="block font-roboto text-md text-gray-900 mb-2">
              Product Description
            </label>
            <div className="bg-white rounded-lg border border-gray-300">
              <ReactQuill
                value={description}
                onChange={handleDescriptionChange}
                modules={modules}
                formats={formats}
                className="h-44 mb-2"
                theme="snow"
                placeholder="Enter product description..."
              />
            </div>
          </div>

          {/* Internal Notes */}
          <div className="w-full">
            <label className="block font-roboto text-md text-gray-900 mb-2">
              Internal Notes
            </label>
            <textarea
              value={internalNotes}
              onChange={(e) => handleEditChange(e)}
              name="internalNotes"
              className="w-full h-44 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter internal notes"
            />
          </div>
          </div>

          {/* Image Upload Section */}
          <div className="col-span-2 mt-5">
            <label className="block font-roboto text-md text-gray-900 mb-2">
              Product Image
            </label>
            <div
              className={`px-6 py-12 border-2 border-dashed rounded-lg text-center transition-colors duration-300 ease-in-out ${
                isDragging
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 bg-white"
              } hover:border-blue-400 focus-within:border-blue-500`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isLoadingSingleImage ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-500">Processing image...</p>
                </div>
              ) : singleImage ? (
                <div className="flex flex-col items-center">
                  <img
                    src={URL.createObjectURL(singleImage)}
                    alt="Selected"
                    className="w-32 h-32 object-cover mb-4 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setSingleImage(null)}
                    className="text-sm text-red-600 underline"
                  >
                    Remove Image
                  </button>
                </div>
              ) : (
                <p className="text-gray-500">
                  Drag & drop a file here or click to select
                </p>
              )}
              <input
                type="file"
                id="fileInput"
                className="hidden"
                onChange={handleFileChange}
                accept="image/*, .heic, .heif"
              />
              <label
                htmlFor="fileInput"
                className="mt-2 inline-block text-blue-600 underline cursor-pointer"
              >
                Browse File
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 flex justify-start bg-gray-50 border-t border-gray-200 sticky bottom-0">
          <button
            onClick={handleUpdateEditProduct}
            className="w-56 py-3 font-roboto bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 transition-all"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProductModal;
